// ============================================================
// 問題データ（構造式ビルダー）
//
// answer.atoms: [{ id, element }]   … 水素も明示的に配置する
// answer.bonds: [{ a, b, order }]   … order = 1(単), 2(二重), 3(三重)
//
// 採点は「原子の種類・数」と「結合の構造（同型）」を照合する。
// ============================================================

export const questions = [
  {
    id: 1,
    title: '水 H₂O',
    difficulty: '基礎',
    category: '分子と結合',
    isFree: true,
    formula: 'H₂O',
    description:
      '水分子の構造式を組み立ててください。\n\n【ヒント】\n・酸素原子1個に水素原子2個が単結合で結合しています。\n・水素も1個ずつ配置して結合で結びましょう。',
    answer: {
      atoms: [
        { id: 'o1', element: 'O' },
        { id: 'h1', element: 'H' },
        { id: 'h2', element: 'H' },
      ],
      bonds: [
        { a: 'o1', b: 'h1', order: 1 },
        { a: 'o1', b: 'h2', order: 1 },
      ],
    },
    explanation:
      '水 H₂O は、酸素原子1個に水素原子2個が単結合した折れ線形の分子です。酸素は2本の共有結合をつくり、非共有電子対を2組もちます。',
  },
  {
    id: 2,
    title: 'アンモニア NH₃',
    difficulty: '基礎',
    category: '分子と結合',
    isFree: true,
    formula: 'NH₃',
    description:
      'アンモニア分子の構造式を組み立ててください。\n\n【ヒント】\n・窒素原子1個に水素原子3個が単結合で結合しています。',
    answer: {
      atoms: [
        { id: 'n1', element: 'N' },
        { id: 'h1', element: 'H' },
        { id: 'h2', element: 'H' },
        { id: 'h3', element: 'H' },
      ],
      bonds: [
        { a: 'n1', b: 'h1', order: 1 },
        { a: 'n1', b: 'h2', order: 1 },
        { a: 'n1', b: 'h3', order: 1 },
      ],
    },
    explanation:
      'アンモニア NH₃ は窒素原子に3個の水素が単結合した三角錐形の分子です。窒素は3本の共有結合と1組の非共有電子対をもち、これが塩基性や配位結合のもとになります。',
  },
  {
    id: 3,
    title: 'メタン CH₄',
    difficulty: '基礎',
    category: '有機化合物',
    isFree: true,
    formula: 'CH₄',
    description:
      '最も単純な炭化水素、メタンの構造式を組み立ててください。\n\n【ヒント】\n・炭素原子1個に水素原子4個が単結合で結合しています。',
    answer: {
      atoms: [
        { id: 'c1', element: 'C' },
        { id: 'h1', element: 'H' },
        { id: 'h2', element: 'H' },
        { id: 'h3', element: 'H' },
        { id: 'h4', element: 'H' },
      ],
      bonds: [
        { a: 'c1', b: 'h1', order: 1 },
        { a: 'c1', b: 'h2', order: 1 },
        { a: 'c1', b: 'h3', order: 1 },
        { a: 'c1', b: 'h4', order: 1 },
      ],
    },
    explanation:
      'メタン CH₄ は炭素1個に水素4個が単結合した正四面体形の分子です。炭素は常に4本の共有結合をつくります（原子価4）。',
  },
  {
    id: 4,
    title: '二酸化炭素 CO₂',
    difficulty: '基礎',
    category: '分子と結合',
    isFree: false,
    formula: 'CO₂',
    description:
      '二酸化炭素の構造式を組み立ててください。\n\n【ヒント】\n・炭素原子1個の両側に酸素原子が「二重結合」で結合しています。\n・結合をダブルにするには、結合モードを「二重結合」にしてから原子どうしをつなぎます。',
    answer: {
      atoms: [
        { id: 'c1', element: 'C' },
        { id: 'o1', element: 'O' },
        { id: 'o2', element: 'O' },
      ],
      bonds: [
        { a: 'c1', b: 'o1', order: 2 },
        { a: 'c1', b: 'o2', order: 2 },
      ],
    },
    explanation:
      '二酸化炭素 CO₂ は O=C=O の直線形分子です。炭素は2本の二重結合（合計4本の共有結合）をつくります。分子全体は無極性です。',
  },
  {
    id: 5,
    title: 'エチレン C₂H₄',
    difficulty: '標準',
    category: '有機化合物',
    isFree: false,
    formula: 'C₂H₄',
    description:
      'アルケンの代表、エチレン（エテン）の構造式を組み立ててください。\n\n【ヒント】\n・炭素原子2個が「二重結合」で結ばれ、各炭素に水素2個が単結合しています。',
    answer: {
      atoms: [
        { id: 'c1', element: 'C' },
        { id: 'c2', element: 'C' },
        { id: 'h1', element: 'H' },
        { id: 'h2', element: 'H' },
        { id: 'h3', element: 'H' },
        { id: 'h4', element: 'H' },
      ],
      bonds: [
        { a: 'c1', b: 'c2', order: 2 },
        { a: 'c1', b: 'h1', order: 1 },
        { a: 'c1', b: 'h2', order: 1 },
        { a: 'c2', b: 'h3', order: 1 },
        { a: 'c2', b: 'h4', order: 1 },
      ],
    },
    explanation:
      'エチレン C₂H₄ は C=C 二重結合をもつ最も簡単なアルケンです。二重結合部分は付加反応を起こしやすく、付加重合によりポリエチレンになります。',
  },
  {
    id: 6,
    title: 'アセチレン C₂H₂',
    difficulty: '標準',
    category: '有機化合物',
    isFree: false,
    formula: 'C₂H₂',
    description:
      'アルキンの代表、アセチレンの構造式を組み立ててください。\n\n【ヒント】\n・炭素原子2個が「三重結合」で結ばれ、各炭素に水素1個が単結合しています。',
    answer: {
      atoms: [
        { id: 'c1', element: 'C' },
        { id: 'c2', element: 'C' },
        { id: 'h1', element: 'H' },
        { id: 'h2', element: 'H' },
      ],
      bonds: [
        { a: 'c1', b: 'c2', order: 3 },
        { a: 'c1', b: 'h1', order: 1 },
        { a: 'c2', b: 'h2', order: 1 },
      ],
    },
    explanation:
      'アセチレン C₂H₂ は H–C≡C–H の直線形分子で、炭素間に三重結合をもちます。三重結合は反応性が高く、付加反応の材料として重要です。',
  },
  {
    id: 7,
    title: 'ホルムアルデヒド HCHO',
    difficulty: '標準',
    category: '官能基',
    isFree: false,
    formula: 'CH₂O',
    description:
      '最も単純なアルデヒド、ホルムアルデヒドの構造式を組み立ててください。\n\n【ヒント】\n・炭素に酸素が「二重結合」（カルボニル基 C=O）、水素2個が単結合しています。',
    answer: {
      atoms: [
        { id: 'c1', element: 'C' },
        { id: 'o1', element: 'O' },
        { id: 'h1', element: 'H' },
        { id: 'h2', element: 'H' },
      ],
      bonds: [
        { a: 'c1', b: 'o1', order: 2 },
        { a: 'c1', b: 'h1', order: 1 },
        { a: 'c1', b: 'h2', order: 1 },
      ],
    },
    explanation:
      'ホルムアルデヒド HCHO はカルボニル基 C=O をもつ最も簡単なアルデヒドです。還元性を示し、フェーリング液を還元、銀鏡反応を示します。',
  },
  {
    id: 8,
    title: 'エタノール C₂H₆O',
    difficulty: '標準',
    category: '官能基',
    isFree: false,
    formula: 'C₂H₆O',
    description:
      'アルコールの代表、エタノールの構造式を組み立ててください。\n\n【ヒント】\n・CH₃–CH₂–OH の並びです。\n・末端の酸素にヒドロキシ基の水素（O–H）を忘れずに結合しましょう。',
    answer: {
      atoms: [
        { id: 'c1', element: 'C' },
        { id: 'c2', element: 'C' },
        { id: 'o1', element: 'O' },
        { id: 'h1', element: 'H' },
        { id: 'h2', element: 'H' },
        { id: 'h3', element: 'H' },
        { id: 'h4', element: 'H' },
        { id: 'h5', element: 'H' },
        { id: 'h6', element: 'H' },
      ],
      bonds: [
        { a: 'c1', b: 'c2', order: 1 },
        { a: 'c2', b: 'o1', order: 1 },
        { a: 'o1', b: 'h6', order: 1 },
        { a: 'c1', b: 'h1', order: 1 },
        { a: 'c1', b: 'h2', order: 1 },
        { a: 'c1', b: 'h3', order: 1 },
        { a: 'c2', b: 'h4', order: 1 },
        { a: 'c2', b: 'h5', order: 1 },
      ],
    },
    explanation:
      'エタノール C₂H₆O はヒドロキシ基 –OH をもつアルコールです。同じ分子式のジメチルエーテルとは構造が異なる「構造異性体」の関係にあります。',
  },
  {
    id: 9,
    title: 'ジメチルエーテル C₂H₆O',
    difficulty: '応用',
    category: '官能基',
    isFree: false,
    formula: 'C₂H₆O',
    description:
      'エタノールと同じ分子式 C₂H₆O をもつ「構造異性体」、ジメチルエーテルを組み立ててください。\n\n【ヒント】\n・CH₃–O–CH₃ の並びで、酸素が2つの炭素の間に入ります。\n・分子式は同じでも、原子のつながり方が違えば別の物質です。',
    answer: {
      atoms: [
        { id: 'c1', element: 'C' },
        { id: 'o1', element: 'O' },
        { id: 'c2', element: 'C' },
        { id: 'h1', element: 'H' },
        { id: 'h2', element: 'H' },
        { id: 'h3', element: 'H' },
        { id: 'h4', element: 'H' },
        { id: 'h5', element: 'H' },
        { id: 'h6', element: 'H' },
      ],
      bonds: [
        { a: 'c1', b: 'o1', order: 1 },
        { a: 'o1', b: 'c2', order: 1 },
        { a: 'c1', b: 'h1', order: 1 },
        { a: 'c1', b: 'h2', order: 1 },
        { a: 'c1', b: 'h3', order: 1 },
        { a: 'c2', b: 'h4', order: 1 },
        { a: 'c2', b: 'h5', order: 1 },
        { a: 'c2', b: 'h6', order: 1 },
      ],
    },
    explanation:
      'ジメチルエーテル CH₃OCH₃ はエーテル結合 C–O–C をもち、エタノールと同じ分子式 C₂H₆O の構造異性体です。–OH をもたないため沸点が低く、水素結合をつくりません。',
  },
  {
    id: 10,
    title: '酢酸 CH₃COOH',
    difficulty: '応用',
    category: '官能基',
    isFree: false,
    formula: 'C₂H₄O₂',
    description:
      'カルボン酸の代表、酢酸の構造式を組み立ててください。\n\n【ヒント】\n・CH₃–C(=O)–O–H の並びです。\n・カルボキシ基 –COOH には、二重結合の酸素（C=O）と、水素のついた酸素（O–H）の2つの酸素があります。',
    answer: {
      atoms: [
        { id: 'c1', element: 'C' },
        { id: 'c2', element: 'C' },
        { id: 'o1', element: 'O' },
        { id: 'o2', element: 'O' },
        { id: 'h1', element: 'H' },
        { id: 'h2', element: 'H' },
        { id: 'h3', element: 'H' },
        { id: 'h4', element: 'H' },
      ],
      bonds: [
        { a: 'c1', b: 'c2', order: 1 },
        { a: 'c2', b: 'o1', order: 2 },
        { a: 'c2', b: 'o2', order: 1 },
        { a: 'o2', b: 'h4', order: 1 },
        { a: 'c1', b: 'h1', order: 1 },
        { a: 'c1', b: 'h2', order: 1 },
        { a: 'c1', b: 'h3', order: 1 },
      ],
    },
    explanation:
      '酢酸 CH₃COOH はカルボキシ基 –COOH をもつ弱酸です。カルボニル基（C=O）とヒドロキシ基（O–H）が同じ炭素に結合しているのが特徴で、水中でわずかに電離します。',
  },
  {
    id: 11,
    title: '窒素 N₂',
    difficulty: '基礎',
    category: '分子と結合',
    isFree: false,
    formula: 'N₂',
    description:
      '空気の約8割を占める窒素分子を組み立ててください。\n\n【ヒント】\n・窒素原子2個が「三重結合」で結ばれています。',
    answer: {
      atoms: [
        { id: 'n1', element: 'N' },
        { id: 'n2', element: 'N' },
      ],
      bonds: [{ a: 'n1', b: 'n2', order: 3 }],
    },
    explanation:
      '窒素 N₂ は N≡N の三重結合をもつ非常に安定な分子です。三重結合の結合エネルギーが大きいため反応性が低く、常温では不活性です。',
  },
  {
    id: 12,
    title: 'ベンゼン C₆H₆',
    difficulty: '応用',
    category: '芳香族',
    isFree: false,
    formula: 'C₆H₆',
    description:
      '芳香族化合物の基本、ベンゼン環を組み立ててください。\n\n【ヒント】\n・炭素6個が環状につながり、単結合と二重結合が交互に並びます（ケクレ構造）。\n・各炭素に水素を1個ずつ結合します。',
    answer: {
      atoms: [
        { id: 'c1', element: 'C' },
        { id: 'c2', element: 'C' },
        { id: 'c3', element: 'C' },
        { id: 'c4', element: 'C' },
        { id: 'c5', element: 'C' },
        { id: 'c6', element: 'C' },
        { id: 'h1', element: 'H' },
        { id: 'h2', element: 'H' },
        { id: 'h3', element: 'H' },
        { id: 'h4', element: 'H' },
        { id: 'h5', element: 'H' },
        { id: 'h6', element: 'H' },
      ],
      bonds: [
        { a: 'c1', b: 'c2', order: 2 },
        { a: 'c2', b: 'c3', order: 1 },
        { a: 'c3', b: 'c4', order: 2 },
        { a: 'c4', b: 'c5', order: 1 },
        { a: 'c5', b: 'c6', order: 2 },
        { a: 'c6', b: 'c1', order: 1 },
        { a: 'c1', b: 'h1', order: 1 },
        { a: 'c2', b: 'h2', order: 1 },
        { a: 'c3', b: 'h3', order: 1 },
        { a: 'c4', b: 'h4', order: 1 },
        { a: 'c5', b: 'h5', order: 1 },
        { a: 'c6', b: 'h6', order: 1 },
      ],
    },
    explanation:
      'ベンゼン C₆H₆ は正六角形の環に単結合と二重結合が交互に並ぶケクレ構造で表されます。実際は電子が環全体に均一に非局在化しており、付加より置換反応を起こしやすい安定な芳香族です。',
  },
]

/** id から問題を取得 */
export const getQuestionById = (id) => questions.find((q) => q.id === id)

/** 問題で使う元素の種類（ヒントモードのパレット用） */
export const getHintElements = (question) => [
  ...new Set((question?.answer?.atoms || []).map((a) => a.element)),
]

/** 無料でアクセスできるか（現状は全ログインユーザーに開放、id<=3は未ログインでも可） */
export const isFreeQuestion = (question) => Boolean(question?.isFree)
